<template>
  <div class="cleano-home">
    <AppNavigation />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
      </div>

      <v-container class="hero-content py-16">
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="text-center text-md-left pr-md-8">
            <div class="hero-eyebrow hero-anim d1 mb-6" :class="{ in: heroReady }">
              <v-icon size="16" color="#A6DBF7">mdi-broom</v-icon>
              <span>Software for cleaning businesses</span>
            </div>

            <h1 class="hero-title hero-anim d2 mb-6" :class="{ in: heroReady }">
              Professional Cleaning Management Made Simple
            </h1>

            <p class="hero-subtitle hero-anim d3 mb-8" :class="{ in: heroReady }">
              Everything your cleaning business needs to run and grow: jobs and crews, quotes, contracts, checklists with photo proof, pay summaries, invoicing, and customer messaging. All for just $150/month.
            </p>

            <div class="cta-buttons hero-anim d4 mb-6" :class="{ in: heroReady }">
              <v-btn
                x-large
                depressed
                class="mr-4 mb-3 cta-btn-primary btn-accent"
                to="/demo"
              >
                <v-icon left size="22" color="#06121C">mdi-rocket-launch</v-icon>
                Request a Demo
              </v-btn>
            </div>

            <div class="trust-indicators hero-anim d5" :class="{ in: heroReady }">
              <div class="trust-badge">
                <v-icon color="white" small>mdi-currency-usd</v-icon>
                <span>Only $150/Month</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-clock-fast</v-icon>
                <span>Live in a Couple of Weeks</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-cancel</v-icon>
                <span>Cancel Anytime</span>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="text-center">
            <div class="phone-mockup-container" :class="{ in: heroReady }">
              <div class="mockup-glow"></div>
              <div class="phone-frame" :class="{ floating: heroReady }">
                <div class="phone-screen">
                  <!-- Status Bar -->
                  <div class="status-bar">
                    <span class="time">9:41</span>
                    <div class="notch"></div>
                    <div class="status-icons">
                      <v-icon x-small color="white">mdi-signal</v-icon>
                      <v-icon x-small color="white">mdi-wifi</v-icon>
                      <v-icon x-small color="white">mdi-battery</v-icon>
                    </div>
                  </div>

                  <!-- App Header -->
                  <div class="app-header">
                    <v-icon color="white" size="20">mdi-menu</v-icon>
                    <div class="header-logo">
                      <v-icon color="white" size="16">mdi-home-variant</v-icon>
                      <span>Cleano</span>
                    </div>
                    <div class="header-actions">
                      <v-icon color="white" size="20">mdi-bell</v-icon>
                      <div class="notification-dot"></div>
                    </div>
                  </div>

                  <!-- Content Area -->
                  <div class="screen-content">
                    <!-- Quick Stats -->
                    <div class="stats-row">
                      <div class="stat-card">
                        <div class="stat-icon" style="background: linear-gradient(135deg, #6FC1EC 0%, #3A93C8 100%);">
                          <v-icon color="white" size="20">mdi-calendar-check</v-icon>
                        </div>
                        <div class="stat-info">
                          <div class="stat-number">12</div>
                          <div class="stat-label">Today</div>
                        </div>
                      </div>
                      <div class="stat-card">
                        <div class="stat-icon" style="background: linear-gradient(135deg, #5B6B7E 0%, #343F4E 100%);">
                          <v-icon color="white" size="20">mdi-account-multiple</v-icon>
                        </div>
                        <div class="stat-info">
                          <div class="stat-number">48</div>
                          <div class="stat-label">Clients</div>
                        </div>
                      </div>
                    </div>

                    <!-- Upcoming Appointment Card -->
                    <div class="appointment-card">
                      <div class="appointment-header">
                        <div class="appointment-time">
                          <v-icon color="#3A8CC2" size="18">mdi-clock-outline</v-icon>
                          <span>2:00 PM Today</span>
                        </div>
                        <span class="status-chip">Confirmed</span>
                      </div>

                      <div class="appointment-client">
                        <div class="client-avatar">
                          <v-icon color="white" size="20">mdi-account</v-icon>
                        </div>
                        <div class="client-details">
                          <div class="client-name">Sarah Johnson</div>
                          <div class="client-address">
                            <v-icon x-small>mdi-map-marker</v-icon>
                            <span>123 Main St, Apt 4B</span>
                          </div>
                        </div>
                      </div>

                      <div class="appointment-service">
                        <div class="service-tag">
                          <v-icon small color="#3A8CC2">mdi-home-variant</v-icon>
                          <span>Deep Clean</span>
                        </div>
                        <div class="service-price">$150</div>
                      </div>

                      <div class="appointment-notes">
                        <v-icon x-small color="#8A94A3">mdi-note-text</v-icon>
                        <span>Allergic to lavender scents. Use unscented products.</span>
                      </div>

                      <div class="appointment-actions">
                        <div class="appt-action">
                          <v-icon small color="#3A8CC2">mdi-message</v-icon>
                          <span>Message</span>
                        </div>
                        <div class="appt-action">
                          <v-icon small color="#3A8CC2">mdi-phone</v-icon>
                          <span>Call</span>
                        </div>
                        <div class="appt-action">
                          <v-icon small color="#3A8CC2">mdi-directions</v-icon>
                          <span>Navigate</span>
                        </div>
                      </div>
                    </div>

                    <!-- Quick Actions -->
                    <div class="quick-actions">
                      <div class="action-button">
                        <div class="action-icon" style="background: linear-gradient(135deg, #6FC1EC 0%, #3A93C8 100%);">
                          <v-icon color="white" size="20">mdi-file-document</v-icon>
                        </div>
                        <span>Send Invoice</span>
                      </div>
                      <div class="action-button">
                        <div class="action-icon" style="background: linear-gradient(135deg, #8AA4BD 0%, #5A758F 100%);">
                          <v-icon color="white" size="20">mdi-star</v-icon>
                        </div>
                        <span>Rewards</span>
                      </div>
                      <div class="action-button">
                        <div class="action-icon" style="background: linear-gradient(135deg, #5B6B7E 0%, #343F4E 100%);">
                          <v-icon color="white" size="20">mdi-bell-ring</v-icon>
                        </div>
                        <span>Reminder</span>
                      </div>
                    </div>
                  </div>

                  <!-- Bottom Navigation -->
                  <div class="bottom-nav">
                    <div class="nav-item active">
                      <v-icon color="#3A8CC2" size="22">mdi-home</v-icon>
                      <div class="nav-indicator"></div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-calendar-month</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-account-multiple</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-message</v-icon>
                      <div class="message-badge">3</div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-cog</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Value Proposition Section -->
    <section class="value-section" data-reveal="valueVisible">
      <v-container>
        <div class="value-box reveal" :class="{ 'is-visible': valueVisible }">
          <div class="value-main">
            <div class="value-icon">
              <v-icon size="34" color="white">mdi-cash-multiple</v-icon>
            </div>
            <div>
              <h2 class="value-title">Everything You Need for Just $150/Month</h2>
              <p class="value-subtitle">
                No hidden fees. No per-user charges. One simple price for unlimited customers, sites and jobs, plus a one-time $499 onboarding where we build and configure it around your business.
              </p>
            </div>
          </div>
          <div class="value-features">
            <div class="value-feature">
              <v-icon color="#A6DBF7" small>mdi-check-circle</v-icon>
              <span>Unlimited Clients &amp; Appointments</span>
            </div>
            <div class="value-feature">
              <v-icon color="#A6DBF7" small>mdi-check-circle</v-icon>
              <span>All Premium Features Included</span>
            </div>
            <div class="value-feature">
              <v-icon color="#A6DBF7" small>mdi-check-circle</v-icon>
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section" data-reveal="featuresVisible">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge reveal mb-4" :class="{ 'is-visible': featuresVisible }">
            <v-icon small color="#5BB4E5">mdi-star-four-points</v-icon>
            <span>POWERFUL FEATURES</span>
          </div>
          <h2 class="section-title reveal d1 mb-4" :class="{ 'is-visible': featuresVisible }">
            Everything Your Cleaning Business Needs
          </h2>
          <p class="section-subtitle reveal d2" :class="{ 'is-visible': featuresVisible }">
            Professional tools for house cleaners, janitorial crews, rental turnovers, carpet, window and exterior pros
          </p>
        </div>

        <v-row class="mb-12">
          <v-col
            v-for="(feature, index) in features"
            :key="index"
            cols="12"
            md="6"
            lg="4"
            class="mb-6"
          >
            <div
              class="feature-card-modern"
              :class="{ 'card-animate-in': featuresVisible }"
              :style="{ 'animation-delay': `${index * 0.08}s` }"
            >
              <div class="feature-card-glow" :style="{ background: feature.gradient }"></div>
              <div class="feature-card-content">
                <div class="feature-icon-modern" :style="{ background: feature.gradient }">
                  <v-icon size="32" color="white">{{ feature.icon }}</v-icon>
                </div>

                <h3 class="feature-title">{{ feature.title }}</h3>
                <p class="feature-description">{{ feature.description }}</p>

                <div class="feature-benefits">
                  <div v-for="(benefit, idx) in feature.benefits" :key="idx" class="benefit-item">
                    <v-icon size="14" color="#A6DBF7">mdi-check-circle</v-icon>
                    <span>{{ benefit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section" data-reveal="benefitsVisible">
      <div class="benefits-background">
        <div class="benefit-shape shape-1"></div>
        <div class="benefit-shape shape-2"></div>
      </div>

      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge reveal mb-4" :class="{ 'is-visible': benefitsVisible }">
            <v-icon small color="#5BB4E5">mdi-shield-star</v-icon>
            <span>WHY CLEANO</span>
          </div>
          <h2 class="section-title reveal d1 mb-4" :class="{ 'is-visible': benefitsVisible }">
            Grow Your Cleaning Business with Confidence
          </h2>
          <p class="section-subtitle reveal d2" :class="{ 'is-visible': benefitsVisible }">
            Join successful cleaning businesses using Cleano Management to streamline operations and delight customers
          </p>
        </div>

        <v-row>
          <v-col
            v-for="(benefit, index) in coreBenefits"
            :key="index"
            cols="12"
            md="6"
            class="mb-6"
          >
            <div
              class="benefit-card"
              :class="{ 'card-animate-in': benefitsVisible }"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
            >
              <div class="benefit-card-inner">
                <div class="benefit-icon-section">
                  <div class="benefit-icon-wrap" :style="{ background: benefit.gradient }">
                    <v-icon size="36" color="white">{{ benefit.icon }}</v-icon>
                  </div>
                  <div class="benefit-badge">{{ benefit.badge }}</div>
                </div>

                <div class="benefit-content-section">
                  <h3 class="benefit-title">{{ benefit.title }}</h3>
                  <p class="benefit-text">{{ benefit.description }}</p>

                  <div class="benefit-points">
                    <div v-for="(point, idx) in benefit.points" :key="idx" class="point-item">
                      <div class="point-dot"></div>
                      <span>{{ point }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section" data-reveal="pricingVisible">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge reveal mb-4" :class="{ 'is-visible': pricingVisible }">
            <v-icon small color="#5BB4E5">mdi-currency-usd</v-icon>
            <span>SIMPLE PRICING</span>
          </div>
          <h2 class="section-title reveal d1 mb-4" :class="{ 'is-visible': pricingVisible }">
            One Price. Everything Included.
          </h2>
          <p class="section-subtitle reveal d2" :class="{ 'is-visible': pricingVisible }">
            No hidden fees, no surprises. Just straightforward pricing that helps your business grow
          </p>
        </div>

        <v-row justify="center">
          <v-col cols="12" xl="11">
            <div class="pricing-card-premium reveal-scale" :class="{ 'is-visible': pricingVisible }">
              <div class="pricing-layout">
                <!-- Summary column: price + CTA -->
                <div class="pricing-summary">
                  <div class="pricing-badge">
                    <v-icon small color="#06121C">mdi-star</v-icon>
                    <span>BEST VALUE</span>
                  </div>

                  <div class="pricing-icon">
                    <v-icon size="40" color="white">mdi-home-variant-outline</v-icon>
                  </div>
                  <h3 class="pricing-plan-name">Cleano Management</h3>
                  <p class="pricing-tagline">Complete cleaning business management platform</p>

                  <div class="pricing-amount-container">
                    <div class="pricing-amount-large">
                      <span class="currency">$</span>
                      <span class="amount">150</span>
                    </div>
                    <div class="pricing-period-label">Per Month</div>
                    <p class="pricing-note">Unlimited customers • All features included • Cancel anytime</p>
                    <p class="pricing-note">+ $499 one-time onboarding &amp; setup</p>
                  </div>

                  <v-btn
                    x-large
                    block
                    depressed
                    class="mb-4 btn-accent"
                    style="font-size: 16px; padding: 28px;"
                    to="/demo"
                  >
                    <v-icon left size="22" color="#06121C">mdi-rocket-launch</v-icon>
                    Request a Demo
                  </v-btn>

                  <div class="pricing-guarantees">
                    <div class="guarantee-item">
                      <v-icon small color="#A6DBF7">mdi-check-decagram</v-icon>
                      <span>30-day money-back guarantee</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#A6DBF7">mdi-check-decagram</v-icon>
                      <span>See a demo before you pay</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#A6DBF7">mdi-check-decagram</v-icon>
                      <span>Custom-built, live in about two weeks</span>
                    </div>
                  </div>
                </div>

                <!-- Features column -->
                <div class="pricing-features-panel">
                  <div class="feature-groups">
                    <div v-for="(group, index) in featureGroups" :key="index" class="feature-group">
                      <div class="group-header">
                        <v-icon color="#5BB4E5" size="18">{{ group.icon }}</v-icon>
                        <h4>{{ group.title }}</h4>
                      </div>
                      <div class="group-features">
                        <div v-for="(feature, idx) in group.features" :key="idx" class="feature-row">
                          <v-icon color="#A6DBF7" size="16">mdi-check-circle</v-icon>
                          <div class="feature-text">
                            <span class="feature-name">{{ feature.name }}</span>
                            <span v-if="feature.detail" class="feature-detail">{{ feature.detail }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Tribzz promo -->
    <section class="tribzz-promo">
      <div class="tp-inner">
        <div class="tp-copy">
          <span class="tp-eyebrow">New · Tribzz for cleaning companies</span>
          <h2 class="tp-title">Give your customers a place to follow your work.</h2>
          <p class="tp-text">Tribzz is our social platform for adults, with no ads and no data sold. Start a Tribzz for your cleaning business to share your work, take appointment requests and quotes, post deals and go live.</p>
          <div class="tp-actions">
            <router-link to="/tribzz" class="tp-btn">See how it works</router-link>
          </div>
        </div>
        <ul class="tp-list">
          <li><v-icon size="20" color="#5BB4E5">mdi-camera-outline</v-icon> Show your work with photos and video</li>
          <li><v-icon size="20" color="#5BB4E5">mdi-file-document-edit-outline</v-icon> Collect job requests and send quotes</li>
          <li><v-icon size="20" color="#5BB4E5">mdi-tag-heart-outline</v-icon> Post deals for the people who follow you</li>
          <li><v-icon size="20" color="#5BB4E5">mdi-advertisements-off</v-icon> No ads, no data sold. Free to start</li>
        </ul>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="final-cta-section">
      <div class="cta-background">
        <div class="cta-orb cta-orb-1"></div>
        <div class="cta-orb cta-orb-2"></div>
      </div>

      <v-container class="text-center">
        <div class="cta-content">
          <v-icon size="64" color="#A6DBF7" class="mb-6">mdi-creation</v-icon>
          <h2 class="cta-title mb-4">
            Ready to Transform Your Cleaning Business?
          </h2>
          <p class="cta-subtitle mb-8">
            Ready to see it built around your cleaning business? Request a demo and we'll show you.
          </p>

          <div class="cta-buttons-final">
            <v-btn
              x-large
              depressed
              class="mr-4 mb-3 btn-accent"
              style="font-size: 16px; padding: 24px 44px;"
              to="/demo"
            >
              <v-icon left size="22" color="#06121C">mdi-rocket-launch</v-icon>
              Request a Demo
            </v-btn>
          </div>
        </div>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
  import AppNavigation from '@/components/AppNavigation.vue'
  import AppFooter from '@/components/AppFooter.vue'

  export default {
    name: 'CleanoHome',
    components: { AppNavigation, AppFooter },

    data: () => ({
      heroReady: false,
      valueVisible: false,
      featuresVisible: false,
      benefitsVisible: false,
      pricingVisible: false,

      features: [
        {
          icon: 'mdi-clipboard-check-outline',
          gradient: 'linear-gradient(135deg, #6FC1EC 0%, #3A93C8 100%)',
          title: 'Jobs, Crews & Dispatch',
          description: 'One-time and recurring jobs assigned to a person or a crew, with routes, reschedules and emergency dispatch handled in one place.',
          benefits: [
            'Recurring schedules generated ahead',
            'Crews, routes and weather holds',
            'Emergency dispatch for restoration calls'
          ]
        },
        {
          icon: 'mdi-file-document-edit-outline',
          gradient: 'linear-gradient(135deg, #3A93C8 0%, #2A6E9C 100%)',
          title: 'Quotes & Estimates',
          description: 'Price flat, hourly, per room or per square foot. Customers accept online and the quote becomes scheduled work.',
          benefits: [
            'Add-ons, tax and deposits built in',
            'Accept or decline from an emailed link',
            'Convert to jobs or a recurring contract'
          ]
        },
        {
          icon: 'mdi-map-marker-radius-outline',
          gradient: 'linear-gradient(135deg, #8AA4BD 0%, #5A758F 100%)',
          title: 'Sites & Customer Profiles',
          description: 'Every customer can have many homes, offices or rental properties, each with its own access notes, checklist and history.',
          benefits: [
            'Access codes, pets, parking and alarm notes',
            'Full job, photo and invoice history',
            'Import homes and staff from a CSV'
          ]
        },
        {
          icon: 'mdi-camera-marker-outline',
          gradient: 'linear-gradient(135deg, #5B6B7E 0%, #343F4E 100%)',
          title: 'Checklists, Photos & GPS',
          description: 'Proof of work your customers can see: task checklists, before and after photos, GPS clock-in and QA inspections.',
          benefits: [
            'Starter checklists for every cleaning line',
            'Photo proof and completion reports',
            'Inspection scores by crew'
          ]
        },
        {
          icon: 'mdi-file-sign',
          gradient: 'linear-gradient(135deg, #2F7FB0 0%, #1D4E71 100%)',
          title: 'Contracts, Invoices & Pay Summaries',
          description: 'Net-30 commercial contracts, online card payments, receivables aging, and a pay summary for every cleaner by hour, job or commission.',
          benefits: [
            'Monthly contract jobs and invoicing',
            'Automatic overdue reminders',
            'Pay summaries with CSV export'
          ]
        },
        {
          icon: 'mdi-bullhorn-outline',
          gradient: 'linear-gradient(135deg, #48566A 0%, #2A3342 100%)',
          title: 'Messaging, Reviews & Marketing',
          description: 'In-app chat, automated confirmations and reminders by email and push, review requests, and campaigns to bring customers back.',
          benefits: [
            'Review requests and rebook nudges',
            'Loyalty points and referrals',
            'Campaigns by customer segment'
          ]
        }
      ],

      coreBenefits: [
        {
          icon: 'mdi-trending-up',
          gradient: 'linear-gradient(135deg, #6FC1EC 0%, #3A93C8 100%)',
          badge: 'GROW YOUR BUSINESS',
          title: 'Win More Work & Get Paid Faster',
          description: 'Quote quickly, convert acceptances into scheduled jobs, and invoice the moment the work is done.',
          points: [
            'Online quotes customers can accept in one click',
            'Request-a-quote page for your website',
            'Pay-online invoices with automatic reminders',
            'Upsell add-ons and seasonal services'
          ]
        },
        {
          icon: 'mdi-clock-fast',
          gradient: 'linear-gradient(135deg, #8AA4BD 0%, #5A758F 100%)',
          badge: 'SAVE TIME',
          title: 'Run Every Line From One Place',
          description: 'House cleaning, offices, rentals, carpet, windows and more share one schedule, one customer list and one set of books.',
          points: [
            'Recurring jobs generated automatically',
            'Rental turnovers created from booking calendars',
            'Contract invoices built from completed visits',
            'One list of customers, sites and crews'
          ]
        },
        {
          icon: 'mdi-shield-check-outline',
          gradient: 'linear-gradient(135deg, #3A93C8 0%, #2A6E9C 100%)',
          badge: 'DELIVER QUALITY',
          title: 'Prove the Work Was Done Right',
          description: 'Checklists, photos, GPS-verified hours and inspection scores mean fewer disputes and happier customers.',
          points: [
            'Photo proof on every job',
            'GPS clock-in and out',
            'QA scores by crew over time',
            'Certifications and safety sheets on file'
          ]
        },
        {
          icon: 'mdi-cellphone-check',
          gradient: 'linear-gradient(135deg, #5B6B7E 0%, #343F4E 100%)',
          badge: 'LOOK LIKE A PRO',
          title: 'Compete With the Big Franchises',
          description: 'Give your customers a branded app, professional quotes and invoices, and completion reports with photos.',
          points: [
            'Your business name and theme on everything',
            'Professional branded quotes and invoices',
            'Mobile apps for staff and customers',
            'Built around your services and pricing'
          ]
        }
      ],

      featureGroups: [
        {
          icon: 'mdi-calendar-check',
          title: 'Scheduling & Dispatch',
          features: [
            { name: 'Job board and calendar', detail: 'By status, crew or service line' },
            { name: 'Recurring schedules', detail: 'Daily to quarterly, generated ahead' },
            { name: 'Routes and weather holds', detail: 'Order stops, hold exterior jobs' },
            { name: 'Emergency dispatch', detail: 'Alert the on-call crew instantly' }
          ]
        },
        {
          icon: 'mdi-account-multiple',
          title: 'Customers & Sites',
          features: [
            { name: 'Unlimited customers and sites', detail: 'Homes, offices, rentals' },
            { name: 'Access and pet notes', detail: 'Codes, parking, alarms' },
            { name: 'Quotes and estimates', detail: 'Flat, hourly, room or sq ft' },
            { name: 'Direct messaging', detail: 'In-app chat with push alerts' }
          ]
        },
        {
          icon: 'mdi-clipboard-check-outline',
          title: 'Field Work & Quality',
          features: [
            { name: 'Job checklists', detail: 'Templates for every cleaning line' },
            { name: 'Before and after photos', detail: 'Attached to the completion report' },
            { name: 'GPS clock-in and out', detail: 'Verified hours on site' },
            { name: 'Inspections and QA scores', detail: 'Track quality by crew' }
          ]
        },
        {
          icon: 'mdi-cash-multiple',
          title: 'Payments & Contracts',
          features: [
            { name: 'Invoices with online payment', detail: 'Cards accepted securely' },
            { name: 'Contracts and net terms', detail: 'Net 15, 30 or 60 for commercial' },
            { name: 'Receivables aging', detail: 'See who owes what and for how long' },
            { name: 'Deposits and fees', detail: 'Late-cancel and lockout rules' }
          ]
        },
        {
          icon: 'mdi-account-group',
          title: 'Team, Pay Summary & Compliance',
          features: [
            { name: 'Pay by hour, job or percentage', detail: 'Per-person rates, CSV export, mark paid' },
            { name: 'Cleaner earnings view', detail: 'Hours, jobs, tips and paid status' },
            { name: 'Certifications and SDS', detail: 'Expiry alerts before they lapse' },
            { name: 'Equipment and supplies', detail: 'Check-out and low-stock alerts' }
          ]
        },
        {
          icon: 'mdi-chart-line',
          title: 'Growth & Reports',
          features: [
            { name: 'Automations', detail: 'Confirmations, reminders, review requests' },
            { name: 'Marketing campaigns', detail: 'Email customers by segment' },
            { name: 'Online request page', detail: 'Zip-code service area, feeds your waitlist' },
            { name: 'Cleaning plans', detail: 'Recurring plans clients subscribe to' },
            { name: 'Referrals and loyalty', detail: 'Reward repeat customers' },
            { name: 'Business reports', detail: 'Revenue by line, per labor hour, quote conversion' }
          ]
        }
      ]
    }),

    mounted() {
      this.heroTimer = setTimeout(() => {
        this.heroReady = true;
      }, 100);

      this.setupScrollAnimations();
    },

    beforeDestroy() {
      clearTimeout(this.heroTimer);
      clearTimeout(this.observeTimer);
      if (this.observer) this.observer.disconnect();
    },

    methods: {
      // Reveals each [data-reveal] section (the attribute holds the data flag
      // to flip) the first time it scrolls into view.
      setupScrollAnimations() {
        const targets = Array.from(this.$el.querySelectorAll('[data-reveal]'));

        // Older browsers: just show everything.
        if (typeof IntersectionObserver === 'undefined') {
          targets.forEach(el => { this[el.dataset.reveal] = true; });
          return;
        }

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this[entry.target.dataset.reveal] = true;
              this.observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

        targets.forEach(el => this.observer.observe(el));
      }
    }
  };
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Cormorant+Garamond:wght@400;500;600;700&display=swap');

  .cleano-home {
    --bg-page: #070a10;
    --bg-alt: #0b0f17;
    --bg-card: #111722;
    --bg-card-hover: #161d2b;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --border-accent: rgba(91, 180, 229, 0.35);
    --text-primary: #EEF3F8;
    --text-secondary: rgba(238, 243, 248, 0.68);
    --text-muted: rgba(238, 243, 248, 0.48);
    --accent: #5BB4E5;
    --accent-light: #A6DBF7;
    --accent-dark: #3A8CC2;
    --on-accent: #06121C;
    --ease: cubic-bezier(0.22, 1, 0.36, 1);
  }

  .cleano-home {
    overflow-x: hidden;
    background: var(--bg-page);
    padding-top: 80px;
    font-family: 'Syne', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-primary);
  }

  .cleano-home h1,
  .cleano-home h2,
  .cleano-home h3,
  .cleano-home .hero-title,
  .cleano-home .section-title,
  .cleano-home .cta-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    letter-spacing: -0.005em;
  }

  .cleano-home :focus-visible {
    outline: 2px solid var(--accent-light);
    outline-offset: 3px;
  }

  /* Unified accent button, used across hero, modal, pricing, CTAs */
  .btn-accent {
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.01em;
    font-size: 14.5px;
    border-radius: 10px !important;
    background: linear-gradient(150deg, var(--accent-light) 0%, var(--accent) 60%, var(--accent-dark) 100%) !important;
    color: var(--on-accent) !important;
    box-shadow: 0 6px 18px rgba(91, 180, 229, 0.26) !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .btn-accent:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(91, 180, 229, 0.36) !important;
  }

  .btn-accent.v-btn--disabled {
    opacity: 0.5;
    box-shadow: none !important;
    transform: none;
  }

  /* ==================== SHARED REVEAL HELPERS ==================== */
  .reveal {
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 0.7s var(--ease), transform 0.7s var(--ease);
  }

  .reveal.is-visible {
    opacity: 1;
    transform: none;
  }

  .reveal-scale {
    opacity: 0;
    transform: scale(0.97);
    transition: opacity 0.8s var(--ease), transform 0.8s var(--ease);
  }

  .reveal-scale.is-visible {
    opacity: 1;
    transform: none;
  }

  .d1 { transition-delay: 0.1s; }
  .d2 { transition-delay: 0.2s; }
  .d3 { transition-delay: 0.35s; }
  .d4 { transition-delay: 0.5s; }
  .d5 { transition-delay: 0.65s; }

  @keyframes card-rise {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  /* ==================== HERO SECTION ==================== */
  .hero-section {
    position: relative;
    min-height: 92vh;
    background: linear-gradient(160deg, #0d131d 0%, #070a10 55%, #04060a 100%);
    display: flex;
    align-items: center;
    overflow: hidden;
    padding-top: 60px;
  }

  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  }

  .gradient-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: 0.3;
  }

  .orb-1 {
    width: 560px;
    height: 560px;
    background: radial-gradient(circle, rgba(91, 180, 229, 0.5) 0%, transparent 70%);
    top: -180px;
    right: -160px;
  }

  .hero-content {
    position: relative;
    z-index: 2;
    color: white;
  }

  .hero-anim {
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 0.8s var(--ease), transform 0.8s var(--ease);
  }

  .hero-anim.in {
    opacity: 1;
    transform: none;
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
  }

  .hero-title {
    font-size: 3.25rem;
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.02em;
  }

  .hero-subtitle {
    font-size: 1.15rem;
    line-height: 1.6;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.72);
    max-width: 34em;
  }

  .cta-btn-primary {
    font-size: 16px;
    padding: 26px 34px !important;
  }

  .trust-indicators {
    display: flex;
    gap: 12px 20px;
    flex-wrap: wrap;
    margin-top: 30px;
  }

  .trust-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
  }

  /* Phone Mockup */
  .phone-mockup-container {
    position: relative;
    display: inline-block;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 1s ease-out 0.4s, transform 1s var(--ease) 0.4s;
  }

  .phone-mockup-container.in {
    opacity: 1;
    transform: none;
  }

  .phone-frame.floating {
    animation: gentle-float 6s infinite ease-in-out 1.4s;
  }

  @keyframes gentle-float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-16px);
    }
  }

  .mockup-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(91, 180, 229, 0.25) 0%, transparent 70%);
    filter: blur(40px);
    animation: pulse-glow 4s infinite ease-in-out;
  }

  @keyframes pulse-glow {
    0%, 100% {
      opacity: 0.5;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 0.8;
      transform: translate(-50%, -50%) scale(1.1);
    }
  }

  .phone-frame {
    position: relative;
    width: 340px;
    height: 690px;
    background: #1a1a1a;
    border-radius: 45px;
    padding: 12px;
    box-shadow:
      0 0 0 2px #2a2a2a,
      0 0 0 8px #1a1a1a,
      0 30px 80px rgba(0, 0, 0, 0.5);
  }

  .phone-screen {
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, #f6f9fc 0%, #e8eef4 100%);
    border-radius: 35px;
    overflow: hidden;
    position: relative;
    text-align: left;
  }

  .status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    background: linear-gradient(135deg, #3A8CC2 0%, #6FC1EC 100%);
    color: white;
    font-size: 13px;
    font-weight: 600;
    position: relative;
  }

  .time {
    flex: 1;
  }

  .notch {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 140px;
    height: 22px;
    background: #1a1a1a;
    border-radius: 0 0 20px 20px;
  }

  .status-icons {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: linear-gradient(135deg, #3A8CC2 0%, #2B3340 100%);
    color: white;
  }

  .header-logo {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 700;
  }

  .header-actions {
    position: relative;
  }

  .notification-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    background: #A6DBF7;
    border-radius: 50%;
    border: 2px solid white;
  }

  .screen-content {
    height: calc(100% - 120px);
    overflow-y: auto;
    background: #f6f9fc;
    padding: 16px;
  }

  .screen-content::-webkit-scrollbar {
    width: 0;
  }

  .stats-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 16px;
  }

  .stat-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    background: white;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(20, 30, 45, 0.08);
  }

  .stat-icon,
  .action-icon {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stat-number {
    font-size: 20px;
    font-weight: 800;
    color: #1c2430;
    line-height: 1;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7686;
    margin-top: 3px;
  }

  .appointment-card {
    background: white;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(20, 30, 45, 0.08);
    margin-bottom: 16px;
  }

  .appointment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
  }

  .appointment-time {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: #1c2430;
  }

  .status-chip {
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(46, 158, 107, 0.12);
    color: #1f7a52;
    font-size: 11px;
    font-weight: 700;
  }

  .appointment-client {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .client-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #5B6B7E 0%, #343F4E 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .client-name {
    font-size: 14px;
    font-weight: 700;
    color: #1c2430;
  }

  .client-address {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 3px;
    font-size: 11px;
    color: #6b7686;
  }

  .appointment-service {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: #f1f6fa;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .service-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1c2430;
  }

  .service-price {
    font-size: 16px;
    font-weight: 800;
    color: #2A6E9C;
  }

  .appointment-notes {
    display: flex;
    gap: 6px;
    align-items: flex-start;
    font-size: 11.5px;
    line-height: 1.5;
    color: #6b7686;
    margin-bottom: 12px;
  }

  .appointment-actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    padding-top: 10px;
    border-top: 1px solid #e6edf3;
  }

  .appt-action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px 4px;
    font-size: 12px;
    font-weight: 600;
    color: #2A6E9C;
  }

  .quick-actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding-bottom: 72px;
  }

  .action-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 12px 6px;
    background: white;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(20, 30, 45, 0.08);
  }

  .action-button span {
    font-size: 11px;
    font-weight: 600;
    color: #4a5666;
    text-align: center;
  }

  .bottom-nav {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 12px 20px 16px;
    background: white;
    border-top: 1px solid #e6edf3;
  }

  .nav-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    color: #6b7686;
  }

  .nav-indicator {
    width: 6px;
    height: 6px;
    background: #3A8CC2;
    border-radius: 50%;
    position: absolute;
    bottom: -8px;
  }

  .message-badge {
    position: absolute;
    top: -4px;
    right: -6px;
    width: 16px;
    height: 16px;
    background: #3A8CC2;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
  }

  /* ==================== VALUE SECTION ==================== */
  .value-section {
    padding: 72px 0 0;
    background: var(--bg-page);
  }

  .value-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    padding: 36px 40px;
    background: var(--bg-card);
    border: 1px solid var(--border-accent);
    border-radius: 20px;
  }

  .value-main {
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1;
  }

  .value-icon {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #6FC1EC 0%, #3A93C8 100%);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  .value-title {
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.2;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .value-subtitle {
    font-size: 1rem;
    line-height: 1.6;
    color: var(--text-secondary);
    margin: 0;
    max-width: 38em;
  }

  .value-features {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
  }

  .value-feature {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  /* ==================== SECTION HEADERS ==================== */
  .section-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    background: rgba(91, 180, 229, 0.12);
    border: 1px solid var(--border-accent);
    border-radius: 30px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    color: var(--accent-light);
  }

  .section-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    line-height: 1.2;
  }

  .section-subtitle {
    font-size: 1.15rem;
    color: var(--text-secondary);
    max-width: 700px;
    margin: 0 auto;
    font-family: 'Syne', sans-serif;
  }

  /* ==================== FEATURES SECTION ==================== */
  .features-section {
    padding: 100px 0 80px;
    background: var(--bg-page);
    position: relative;
  }

  .feature-card-modern {
    position: relative;
    background: var(--bg-card);
    border-radius: 18px;
    padding: 40px 32px;
    height: 100%;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    transition: transform 0.3s var(--ease), background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  }

  .feature-card-modern.card-animate-in,
  .benefit-card.card-animate-in {
    animation: card-rise 0.7s var(--ease) backwards;
  }

  /* Cards stay hidden until their section scrolls into view */
  .feature-card-modern:not(.card-animate-in),
  .benefit-card:not(.card-animate-in) {
    opacity: 0;
  }

  .feature-card-modern:hover {
    transform: translateY(-4px);
    background: var(--bg-card-hover);
    border-color: var(--border-accent);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  }

  .feature-card-modern:hover .feature-card-glow {
    opacity: 0.15;
  }

  .feature-card-glow {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  .feature-card-content {
    position: relative;
    z-index: 2;
  }

  .feature-icon-modern {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  .feature-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.3;
  }

  .feature-description {
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 20px;
    font-family: 'Syne', sans-serif;
  }

  .feature-benefits {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.5;
    font-family: 'Syne', sans-serif;
  }

  .benefit-item .v-icon {
    margin-top: 3px;
  }

  /* ==================== BENEFITS SECTION ==================== */
  .benefits-section {
    position: relative;
    padding: 100px 0;
    background: linear-gradient(160deg, #0b0f17 0%, #070a10 100%);
    overflow: hidden;
  }

  .benefits-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  }

  .benefit-shape {
    position: absolute;
    border-radius: 50%;
    background: rgba(91, 180, 229, 0.08);
    filter: blur(60px);
  }

  .shape-1 {
    width: 500px;
    height: 500px;
    top: -100px;
    left: -100px;
  }

  .shape-2 {
    width: 400px;
    height: 400px;
    bottom: -100px;
    right: -100px;
  }

  .benefit-card {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    height: 100%;
    overflow: hidden;
    transition: transform 0.3s var(--ease), border-color 0.3s ease;
  }

  .benefit-card:hover {
    transform: translateY(-4px);
    border-color: var(--border-accent);
  }

  .benefit-card-inner {
    padding: 36px;
  }

  .benefit-icon-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
  }

  .benefit-icon-wrap {
    flex-shrink: 0;
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  .benefit-badge {
    padding: 6px 16px;
    background: rgba(91, 180, 229, 0.12);
    border: 1px solid var(--border-accent);
    color: var(--accent-light);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    text-align: center;
  }

  .benefit-title {
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.3;
  }

  .benefit-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 20px;
    font-family: 'Syne', sans-serif;
  }

  .benefit-points {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .point-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Syne', sans-serif;
  }

  .point-dot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    background: linear-gradient(135deg, #A6DBF7 0%, #5BB4E5 100%);
    border-radius: 50%;
    margin-top: 9px;
  }

  /* ==================== PRICING SECTION ==================== */
  .pricing-section {
    padding: 100px 0;
    background: var(--bg-page);
  }

  .pricing-card-premium {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  }

  .pricing-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    margin-bottom: 24px;
    background: linear-gradient(135deg, #A6DBF7 0%, #5BB4E5 100%);
    color: var(--on-accent);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    box-shadow: 0 4px 12px rgba(91, 180, 229, 0.35);
  }

  .pricing-layout {
    display: grid;
    grid-template-columns: 340px 1fr;
  }

  .pricing-summary {
    background: linear-gradient(160deg, #111722 0%, #070a10 100%);
    padding: 48px 36px 56px;
    text-align: center;
    color: white;
    border-right: 1px solid var(--border-accent);
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .pricing-icon {
    margin-bottom: 16px;
  }

  .pricing-plan-name {
    font-size: 1.7rem;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .pricing-tagline {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 24px;
    font-family: 'Syne', sans-serif;
  }

  .pricing-amount-container {
    margin-bottom: 28px;
  }

  .pricing-amount-large {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 6px;
  }

  .currency {
    font-size: 2rem;
    font-weight: 700;
    margin-right: 4px;
    margin-top: 6px;
    color: var(--accent-light);
  }

  .amount {
    font-size: 3.75rem;
    font-weight: 800;
    line-height: 1;
    color: var(--accent-light);
  }

  .pricing-period-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 14px;
    font-family: 'Syne', sans-serif;
  }

  .pricing-note {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    font-family: 'Syne', sans-serif;
  }

  .pricing-features-panel {
    padding: 48px 40px;
  }

  .feature-groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 32px 36px;
  }

  .group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-subtle);
  }

  .group-header h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }

  .group-features {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .feature-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .feature-row .v-icon {
    margin-top: 2px;
  }

  .feature-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .feature-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
    font-family: 'Syne', sans-serif;
  }

  .feature-detail {
    font-size: 0.8rem;
    color: var(--text-muted);
    line-height: 1.4;
    font-family: 'Syne', sans-serif;
  }

  .pricing-guarantees {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 14px;
    border: 1px solid var(--border-subtle);
    width: 100%;
    margin-top: 8px;
  }

  .guarantee-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
    font-family: 'Syne', sans-serif;
  }

  /* ==================== FINAL CTA SECTION ==================== */
  .final-cta-section {
    position: relative;
    padding: 120px 0;
    background: linear-gradient(160deg, #111722 0%, #070a10 60%, #04060a 100%);
    overflow: hidden;
  }

  .cta-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .cta-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(91, 180, 229, 0.25) 0%, transparent 70%);
    filter: blur(60px);
  }

  .cta-orb-1 {
    width: 400px;
    height: 400px;
    top: -100px;
    left: -100px;
  }

  .cta-orb-2 {
    width: 500px;
    height: 500px;
    bottom: -150px;
    right: -150px;
  }

  .cta-content {
    position: relative;
    z-index: 2;
  }

  .cta-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
    color: var(--text-primary);
  }

  .cta-subtitle {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 700px;
    margin: 0 auto 40px;
    line-height: 1.6;
    font-family: 'Syne', sans-serif;
  }

  .cta-buttons-final {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }

  /* ==================== RESPONSIVE STYLES ==================== */
  @media (max-width: 1264px) {
    .hero-title {
      font-size: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
    }

    .cta-title {
      font-size: 2.75rem;
    }

    .value-box {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  @media (max-width: 960px) {
    .pricing-layout {
      grid-template-columns: 1fr;
    }

    .pricing-summary {
      border-right: none;
      border-bottom: 1px solid var(--border-accent);
    }

    .feature-groups {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }

    .hero-section {
      min-height: auto;
      padding: 80px 0 60px;
    }

    .hero-title {
      font-size: 2.5rem;
      text-align: center;
    }

    .hero-subtitle {
      font-size: 1.1rem;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }

    .trust-indicators {
      justify-content: center;
    }

    .phone-frame {
      width: 280px;
      height: 570px;
    }

    .section-title {
      font-size: 2rem;
    }

    .section-subtitle {
      font-size: 1.1rem;
    }

    .value-section {
      padding-top: 48px;
    }

    .features-section,
    .benefits-section,
    .pricing-section {
      padding: 60px 0;
    }

    .final-cta-section {
      padding: 80px 0;
    }

    .cta-title {
      font-size: 2.25rem;
    }

    .cta-subtitle {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 600px) {
    .hero-title {
      font-size: 2rem;
    }

    .hero-subtitle {
      font-size: 1rem;
    }

    .cta-btn-primary {
      font-size: 16px;
      padding: 20px 32px !important;
    }

    .trust-badge {
      font-size: 12px;
      padding: 6px 12px;
    }

    .section-title {
      font-size: 1.75rem;
    }

    .section-subtitle {
      font-size: 1rem;
    }

    .value-box {
      padding: 28px 24px;
      gap: 24px;
    }

    .value-main {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .value-title {
      font-size: 1.6rem;
    }

    .value-feature {
      white-space: normal;
    }

    .feature-card-modern {
      padding: 28px 24px;
    }

    .feature-title {
      font-size: 1.35rem;
    }

    .benefit-card-inner {
      padding: 28px 24px;
    }

    .benefit-title {
      font-size: 1.4rem;
    }

    .pricing-summary {
      padding: 40px 24px;
    }

    .pricing-plan-name {
      font-size: 1.5rem;
    }

    .amount {
      font-size: 3rem;
    }

    .pricing-features-panel {
      padding: 32px 24px;
    }

    .feature-groups {
      grid-template-columns: 1fr;
      gap: 28px;
    }

    .cta-title {
      font-size: 1.9rem;
    }

    .cta-subtitle {
      font-size: 1.05rem;
    }

    .cta-buttons-final {
      flex-direction: column;
    }
  }

  /* ==================== REDUCED MOTION ==================== */
  @media (prefers-reduced-motion: reduce) {
    .reveal,
    .reveal-scale,
    .hero-anim,
    .phone-mockup-container,
    .feature-card-modern:not(.card-animate-in),
    .benefit-card:not(.card-animate-in) {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }

    .phone-frame.floating,
    .mockup-glow,
    .feature-card-modern.card-animate-in,
    .benefit-card.card-animate-in {
      animation: none !important;
    }

    .feature-card-modern:hover,
    .benefit-card:hover,
    .btn-accent:hover {
      transform: none;
    }
  }

/* Tribzz promo */
.tribzz-promo { padding: 72px 24px; background: #0d121b; border-top: 1px solid rgba(255,255,255,.08); border-bottom: 1px solid rgba(255,255,255,.08); }
.tp-inner { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 1.2fr 1fr; gap: 48px; align-items: center; }
.tp-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #A6DBF7; }
.tp-title { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: clamp(1.9rem, 3.6vw, 2.6rem); line-height: 1.12; color: #EEF3F8; margin: 12px 0 14px; }
.tp-text { color: rgba(238,243,248,.72); line-height: 1.75; margin: 0 0 24px; max-width: 560px; }
.tp-btn { display: inline-flex; align-items: center; height: 50px; padding: 0 26px; border-radius: 999px; background: #5BB4E5; color: #06121C; font-weight: 700; text-decoration: none; transition: background .18s ease, transform .18s ease; }
.tp-btn:hover { background: #A6DBF7; transform: translateY(-1px); }
.tp-list { list-style: none; margin: 0; padding: 24px; display: grid; gap: 14px; background: #111722; border: 1px solid rgba(255,255,255,.09); border-radius: 22px; color: #EEF3F8; }
.tp-list li { display: flex; align-items: center; gap: 12px; font-size: 15px; }
@media (max-width: 860px) { .tp-inner { grid-template-columns: 1fr; gap: 28px; } .tribzz-promo { padding: 52px 20px; } }
</style>